import React, { useMemo } from 'react';
import { useStyles } from './styles';
import {
  IServiceCategoryDataGrid,
  IServiceCategoryDataGridWrapper,
} from './interfaces';
import { FormHelperText, Grid, Typography } from '@material-ui/core';
import PaginatedDataGrid from 'components/PaginatedDataGrid';
import { GridCellParams, GridColumns } from '@material-ui/data-grid';
import CustomActivationCheckbox from './CustomActivationCheckbox';
import CustomSelectPriority from './CustomSelectPriority';
import CustomCollapsibleList from './CustomCollapsibleList';
import { ErrorMessage, useFormikContext, getIn } from 'formik';
import { IServicesCategoryMailingList } from 'components/StrategyRegister/interfaces';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

const ServiceCategoryDataGridWrap: React.FC<IServiceCategoryDataGridWrapper> = ({
  serviceCategory,
  isViewOnly,
}) => {
  const { errors } = useFormikContext<IServicesCategoryMailingList>();

  const { lists } = useSelector(
    (state: RootState) => state.mailingRuleByServiceCategoryList,
  );

  const { current } = useSelector((state: RootState) => state.strategy);

  let list = lists[serviceCategory._id] || [];

  if (isViewOnly) {
    list = list.map((item) => {
      if (
        current.selectedServiceCategories[serviceCategory._id] &&
        current.selectedServiceCategories[serviceCategory._id][item.id]
      ) {
        return {
          ...item,
          priority:
            current.selectedServiceCategories[serviceCategory._id][item.id]
              .priority,
        };
      }
      return item;
    });
  }

  const selfErrors = getIn(errors, `selectedServiceCategories`);

  const serviceCategoryMemo = useMemo(
    () => (
      <ServiceCategoryDataGrid
        serviceCategory={serviceCategory}
        isViewOnly={isViewOnly}
        selfErrors={selfErrors}
        list={list}
      />
    ),
    [JSON.stringify(selfErrors)],
  );

  return serviceCategoryMemo;
};

const ServiceCategoryDataGrid: React.FC<IServiceCategoryDataGrid> = ({
  serviceCategory,
  isViewOnly,
  selfErrors,
  list,
}: IServiceCategoryDataGrid) => {
  const classes = useStyles();
  const columns: GridColumns = [
    {
      field: 'activation',
      headerName: 'Ativação',
      width: 130,
      renderCell: (params: GridCellParams) =>
        CustomActivationCheckbox(params, serviceCategory._id, isViewOnly),
      sortable: isViewOnly ? true : false,
    },
    {
      field: 'priority',
      headerName: 'Prioridade',
      width: 140,
      renderCell: (params: GridCellParams) =>
        CustomSelectPriority(params, serviceCategory._id, list, isViewOnly),
      sortable: isViewOnly ? true : false,
      sortComparator: (v1, v2, cellParams1, cellParams2) => {
        const value1 = Number(v1);
        const value2 = Number(v2);
        return value1 - value2;
      },
    },
    {
      field: 'mailingRuleName',
      headerName: 'Nome da Régua Mailing',
      width: 300,
      sortComparator: (v1, v2) => {
        const value1 = String(v1);
        const value2 = String(v2);
        return value1.localeCompare(value2);
      },
    },
    {
      field: 'epsList',
      headerName: 'EPSs',
      width: 400,
      renderCell: (params: GridCellParams) => {
        const rowId = params.api.getRowIndex(params.row.id);
        return CustomCollapsibleList(list[rowId].epsList);
      },
      sortComparator: (v1, v2) => {
        const eps1 = v1[0]?.description?.toLowerCase() || '';
        const eps2 = v2[0]?.description?.toLowerCase() || '';
        return eps1.localeCompare(eps2);
      },
    },
    {
      field: 'segment',
      headerName: 'Segmento',
      width: 300,
      sortComparator: (v1, v2) => {
        return v1.localeCompare(v2);
      },
    },
    {
      field: 'distributionKey',
      headerName: 'Chave de distribuição',
      width: 220,
      sortComparator: (v1, v2) => {
        return v1.localeCompare(v2);
      },
    },
    {
      field: 'contractDelay',
      headerName: 'Atraso do contrato',
      width: 250,
      sortComparator: (v1, v2) => {
        const match1 = v1.match(/\d+/g);
        const match2 = v2.match(/\d+/g);
        const number1 = match1 && match1[1] ? Number(match1[1]) : 0;
        const number2 = match2 && match2[1] ? Number(match2[1]) : 0;
        return number1 - number2;
      },
    },
  ];

  let errorMessageProps = {
    name: '',
    render: () => ' ',
  };

  if (
    selfErrors?.grid ||
    typeof selfErrors?.[serviceCategory._id] === 'string'
  ) {
    errorMessageProps = {
      name: selfErrors?.grid
        ? 'selectedServiceCategories'
        : `selectedServiceCategories.${serviceCategory._id}`,
      render: () =>
        selfErrors?.grid ? selfErrors.grid : selfErrors[serviceCategory._id],
    };
  }

  return (
    <Grid
      data-cy="strategy-register__container--service-category-datagrid"
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
      className={classes.containerDatagrid}
    >
      <Grid container direction="row" justifyContent="flex-start">
        <Grid item xs={12}>
          <Typography variant="h6">{serviceCategory.description}</Typography>
        </Grid>
        <Grid item xs={12} className={classes.datagrid}>
          <FormHelperText error>
            {errorMessageProps.name.length > 0 ? (
              <ErrorMessage {...errorMessageProps} />
            ) : (
              ' '
            )}
          </FormHelperText>
          <PaginatedDataGrid
            columns={columns}
            rows={list}
            columnBuffer={columns.length}
            paginationMode="client"
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ServiceCategoryDataGridWrap;
